import React, { useMemo } from 'react';
import { BRAND_TIFFANY_BLUE } from '../../constants/branding';

type Props = {
  /** 表示する月（'YYYY-MM'） */
  month: string;
  /** 月移動 */
  onMonthChange: (nextMonth: string) => void;

  /** 選択中日付（'YYYY-MM-DD'） */
  selectedDate?: string | null;
  /** 予約可能日のSet（'YYYY-MM-DD'） */
  openDateSet: Set<string>;

  /** 日付クリック */
  onSelectDate: (ymd: string) => void;

  /** ナビゲーションの上限/下限（月単位 'YYYY-MM'） */
  minMonth?: string;
  maxMonth?: string;

  /** 週の開始（0=Sun, 1=Mon） */
  weekStartsOn?: 0 | 1;
};

const pad2 = (n: number) => String(n).padStart(2, '0');

const toMonthKey = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}`;
const toYMD = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const addMonths = (monthKey: string, delta: number) => {
  const [y, m] = monthKey.split('-').map(Number);
  const dt = new Date(y, (m - 1) + delta, 1);
  return toMonthKey(dt);
};

const clampMonth = (m: string, min?: string, max?: string) => {
  if (min && m < min) return min;
  if (max && m > max) return max;
  return m;
};

const weekdayLabelsMon = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const weekdayLabelsSun = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const ReserveCalendarGrid: React.FC<Props> = ({
  month,
  onMonthChange,
  selectedDate,
  openDateSet,
  onSelectDate,
  minMonth,
  maxMonth,
  weekStartsOn = 1,
}) => {
  const { year, monIndex } = useMemo(() => {
    const [y, m] = month.split('-').map(Number);
    return { year: y, monIndex: m - 1 };
  }, [month]);

  const firstOfMonth = useMemo(() => new Date(year, monIndex, 1), [year, monIndex]);

  const days = useMemo(() => {
    // month grid: 6 rows * 7 cols
    const startDow = firstOfMonth.getDay(); // 0..6 (Sun..Sat)
    const offset = weekStartsOn === 1
      ? (startDow === 0 ? 6 : startDow - 1) // Monday-start
      : startDow; // Sunday-start

    const gridStart = new Date(year, monIndex, 1 - offset);

    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      return d;
    });
  }, [firstOfMonth, year, monIndex, weekStartsOn]);

  const monthLabel = useMemo(() => {
    const dt = new Date(year, monIndex, 1);
    return dt.toLocaleString('en-US', { month: 'long', year: 'numeric' });
  }, [year, monIndex]);

  const prevMonth = clampMonth(addMonths(month, -1), minMonth, maxMonth);
  const nextMonth = clampMonth(addMonths(month, 1), minMonth, maxMonth);

  const canPrev = !minMonth || prevMonth >= minMonth;
  const canNext = !maxMonth || nextMonth <= maxMonth;

  const weekdayLabels = weekStartsOn === 1 ? weekdayLabelsMon : weekdayLabelsSun;

  return (
    <div className="rounded-[28px] border border-[#d7efee] bg-[linear-gradient(180deg,rgba(244,253,252,0.98),rgba(255,255,255,0.98))] p-4 shadow-[0_18px_40px_rgba(10,186,181,0.1)]">
      {/* header */}
      <div className="flex items-center justify-between mb-3">
        <button
          type="button"
          onClick={() => canPrev && onMonthChange(prevMonth)}
          disabled={!canPrev}
          className={[
            'px-3 py-1 rounded-full text-sm border font-medium',
            canPrev
              ? 'bg-white text-[#087f7c] border-[#cdeceb] hover:bg-[#f1fbfa]'
              : 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed',
          ].join(' ')}
        >
          ←
        </button>

        <div className="text-sm font-semibold text-slate-800">{monthLabel}</div>

        <button
          type="button"
          onClick={() => canNext && onMonthChange(nextMonth)}
          disabled={!canNext}
          className={[
            'px-3 py-1 rounded-full text-sm border font-medium',
            canNext
              ? 'bg-white text-[#087f7c] border-[#cdeceb] hover:bg-[#f1fbfa]'
              : 'bg-slate-50 text-slate-300 border-slate-100 cursor-not-allowed',
          ].join(' ')}
        >
          →
        </button>
      </div>

      {/* weekday */}
      <div className="grid grid-cols-7 gap-2 mb-2">
        {weekdayLabels.map((w) => (
          <div key={w} className="text-center text-[11px] text-slate-400 font-semibold">
            {w}
          </div>
        ))}
      </div>

      {/* grid */}
      <div className="grid grid-cols-7 gap-2">
        {days.map((d) => {
          const ymd = toYMD(d);
          const inMonth = d.getMonth() === monIndex;
          const isOpen = openDateSet.has(ymd);
          const isSelected = selectedDate === ymd;

          const disabled = !inMonth || !isOpen;

          return (
            <button
              key={ymd}
              type="button"
              onClick={() => {
                if (disabled) return;
                onSelectDate(ymd);
              }}
              disabled={disabled}
              className={[
                'h-10 rounded-xl text-sm border transition font-medium',
                !inMonth
                  ? 'bg-slate-50 text-slate-200 border-slate-100 cursor-not-allowed'
                : !isOpen
                  ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                : isSelected
                  ? 'text-white border-transparent shadow-[0_10px_20px_rgba(10,186,181,0.24)]'
                  : 'bg-white text-slate-700 border-[#d5ecea] hover:bg-[#f1fbfa] hover:border-[#9ddfdc]',
              ].join(' ')}
              style={isSelected ? { backgroundColor: BRAND_TIFFANY_BLUE } : undefined}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex items-center gap-3 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: BRAND_TIFFANY_BLUE }} />
          Selected
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-white border border-slate-300" />
          Available
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-slate-300" />
          Closed
        </span>
      </div>
    </div>
  );
};

export default ReserveCalendarGrid;
